import {observer} from 'mobx-react-lite';
import {
  Accordion, AccordionDetails, AccordionSummary, Button,
  Checkbox, CircularProgress,
  Dialog, DialogActions,
  DialogContent, LinearProgress,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import {MergeDocumentDialogProps} from '../../../../entity/props/title/merge-document-dialog-props';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, {useEffect, useState} from 'react';
import {Section} from '../../../../entity/resource/section';
import {Document} from '../../../../entity/resource/document';
import {DocumentDto} from '../../../../entity/dto/title-template-dto';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {useService} from '../../../../infrastructure/service/service';
import {toast} from 'react-toastify';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';

import '../../../../assets/css/style.css';

const MergeDocumentDialog = (props: MergeDocumentDialogProps): JSX.Element => {
  const {documentService, titleService, auditLogsService} = useService();

  const [sectionAccordion, setSectionAccordion] = useState<boolean>(true);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedSections, setSelectedSections] = useState<Map<string, string[]>>(new Map());
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
  const [batchId, setBatchId] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [lastStateMessage, setLastStateMessage] = useState<string>('');

  useEffect(() => {
    setSelectAll(true);
    handleSelectAll();
  }, []);

  const handleSelectAll = (): void => {
    if (selectAll)
      setSelectedSections(new Map());
    else {
      const sectionMap = new Map(props.selectedTitle.sections.map((section: Section): [string, string[]] => [section.id, section.documents.map((document: Document): string => document.id)]));
      setSelectedSections(sectionMap);
    }

    setSelectAll(!selectAll);
  }

  const handleSectionFilter = (sectionId: string, documents: DocumentDto[]): void => {
    const updatedMap = new Map(selectedSections);

    if (selectedSections.has(sectionId)) {
      updatedMap.delete(sectionId);
      setSelectedSections(updatedMap);
    } else {
      const documentIds: string[] = documents.map((document: DocumentDto): string => document.id);
      updatedMap.set(sectionId, documentIds);
    }

    setSelectedSections(updatedMap);
  }

  const isSelectedDocument = (sectionId: string, documentId: string): boolean => {
    if (selectedSections.has(sectionId)) {
      const documents = selectedSections.get(sectionId) ?? [];
      return documents.includes(documentId);
    }

    return false;
  }

  const handleDocumentFilter = (event: any, section: Section, documentId: string): void => {
    event.stopPropagation();

    const documentIds = selectedSections.get(section.id) as string[] ?? [];
    const updatedMap = new Map(selectedSections);

    const updatedDocumentIds = documentIds.includes(documentId) ? documentIds.filter((id: string): boolean => id !== documentId) : [...documentIds, documentId];
    if (updatedDocumentIds.length > 0)
      updatedMap.set(section.id, updatedDocumentIds);
    else
      updatedMap.delete(section.id);

    setSelectedSections(updatedMap);
  }

  const handleMergePdf = async (): Promise<void> => {
    setSubmitLoading(true);
    setProgress(5);
    setMessage('Your request is being processed');

    const batchId = crypto.randomUUID();
    documentService.createMergePdf(
      props.project.createdByOrganizationID,
      props.selectedTitle.id,
      Array.from(selectedSections.values()).flat(),
      batchId);

    await fetchProgress(batchId);
  }

  const fetchProgress = async (batchId: string): Promise<void> => {
    let previousProgress = 0;
    let progressUnchangedDuration = 0;
    const maxUnchangedDuration = 20000;
    const interval = 5000;
    const maxUnchangedCycles = maxUnchangedDuration / interval;

    const intervalId = setInterval(async (): Promise<void> => {
      const response = await auditLogsService.getMergeDocumentsLogs(props.project.createdByOrganizationID, batchId);
      if (response.progress > progress)
        setProgress(response.progress);

      setMessage(response.message);

      if (response.progress === 90) {
        clearInterval(intervalId);

        setSubmitLoading(false);
        setProgress(100);
        setMessage('PDF merged successfully !!');
        toast.success('PDF merged successfully !!');

        await titleService.getTitleById(props.project.createdByOrganizationID, props.selectedTitle.id);

        setTimeout((): void => props.setOpen(false), 1000);
      } else if (response.progress === 30) {
        toast.error(response.message);
        setSubmitLoading(false);

        setTimeout((): void => props.setOpen(false), 1000);
        clearInterval(intervalId);
      }

      if (response.progress !== previousProgress) {
        previousProgress = response.progress;
        progressUnchangedDuration = 0;
      } else
        progressUnchangedDuration++;

      if (progressUnchangedDuration >= maxUnchangedCycles) {
        setSubmitLoading(false);
        setProgressRefresh(true);
        setBatchId(batchId);
        setLastStateMessage(response.message);

        setMessage('Merging your PDFs. Please refresh to check the progress.');
        toast.info('Merging your PDFs. Please refresh to check the progress.');
        clearInterval(intervalId);
      }
    }, interval);
  }

  const handleProgressRefresh = async (): Promise<void> => {
    setProgressRefresh(false);
    setMessage(lastStateMessage);
    setProgress((prevState: number): number => prevState + 1);

    await fetchProgress(batchId);
  }

  const renderSection = (section: Section, index: number): JSX.Element => {
    const hasDocuments = section.documents && section.documents.length > 0;
    const isExpanded = expandedSection === index;

    return (
      <Accordion
        key={index}
        expanded={isExpanded}
        className={`template-list-item ${index % 2 === 0 ? 'even' : 'odd'} ${index === props.selectedTitle.sections.length - 1 ? 'last' : ''} ${isExpanded ? 'parent' : ''}`}>
        <AccordionSummary expandIcon={ hasDocuments ? <ExpandMoreIcon onClick={(): void => setExpandedSection(expandedSection === index ? null : index)}/> : null}>
          <Box className={'checkbox-template-container parent template-checkbox'}>
            <Checkbox
              checked={selectedSections.has(section.id)}
              onClick={() => handleSectionFilter(section.id, [...section.documents.map((document: Document): DocumentDto => ({ id: document.id, name: document.name, url: document.url}))])}/>
            <Typography className={'dialog-icon'}>{section.name}</Typography>
          </Box>
        </AccordionSummary>
        {
          hasDocuments &&
            <AccordionDetails>
              {section.documents.map((document: Document): JSX.Element => renderDocument(section, document.id, document.name, document.url))}
            </AccordionDetails>
        }
      </Accordion>
    );
  }

  const renderDocument = (section: Section, documentId: string, documentName: string, documentUrl: string): JSX.Element => {
    return (
      <Box className={'template-list-item child'}>
        <Box className={'checkbox-template-container template-checkbox'} onClick={(): Window | null => window.open(documentUrl)}>
          <Checkbox checked={isSelectedDocument(section.id, documentId)} onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleDocumentFilter(event, section, documentId)}/>
          <PictureAsPdfIcon className={'zitles-icon checkbox-template-container icon'}/>
          <Typography className={'book-and-page-header'}>{documentName}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>Merge Documents</span>
        <IconButton onClick={(): void => props.setOpen(false)}>
          <CloseIcon className={'dialog-icon'}/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Paper elevation={0}>
          <Accordion
            elevation={0}
            className={'template-dialog-card'}
            expanded={sectionAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setSectionAccordion(!sectionAccordion)} className={'dialog-icon'}/>}>
              <Typography className={'template-data-value accordion'}>Sections and Documents</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={'checkbox-template-container header template-checkbox'}>
                <Checkbox checked={selectAll} onClick={handleSelectAll}/>
                <Typography className={'checkbox-template-container title'}>{ selectAll ? 'Deselect All' : 'Select All'}</Typography>
              </Box>
              {
                props.selectedTitle.sections.filter((section: Section): boolean => section.name !== 'Unclassified').map(renderSection)
              }
            </AccordionDetails>
          </Accordion>
        </Paper>
      </DialogContent>
      <DialogActions className={'action-btn-container'}>
        {
          progress > 0
            ? <Box className={'progress-zone'}>
              <Box className={'progress-icon-zone'}>
                {
                  progressRefresh
                    ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                    : <InsertDriveFileIcon className={'progress-icon'} />
                }
              </Box>
              <Box className={'progress-bar-zone'}>
                <Typography className={'progress-bar-title'}>{message}</Typography>
                <Box className={'progress-bar-container'}>
                  <Box className={'progress-bar'}>
                    <LinearProgress variant='determinate' {...props} value={progress} color='success' className={'progress'}/>
                  </Box>
                  <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                </Box>
              </Box>
            </Box>
            : <Box>
                <Button
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  disabled={submitLoading || Array.from(selectedSections.values()).flat().length < 2}
                  className={`zitles-button template submit ${submitLoading || Array.from(selectedSections.values()).flat().length < 2 ? 'disable' : ''}`}
                  onClick={handleMergePdf}
                  startIcon={submitLoading ? <CircularProgress size={14} color='inherit' /> : null}>
                  {
                    submitLoading ? 'Submitting' : 'Submit'
                  }
                </Button>
              </Box>
        }
      </DialogActions>
    </Dialog>
  );
}

export default observer(MergeDocumentDialog);
